<script setup lang="ts">
  import type { HalfContentHalfImageStoryblok } from '@/storyblok/component-types-sb'
  import { cva } from 'class-variance-authority'
  import { toAssetImageProps } from '@/utils/storyblok/normalizers'

  const props = defineProps<{
    blok: HalfContentHalfImageStoryblok
  }>()

  const { isDesktop, isTablet } = useBTBreakpoints()

  const imageDimensions = {
    small: {
      width: {
        desktop: 381,
        tablet: 288,
        mobile: 320
      },
      height: {
        desktop: 254,
        tablet: 184,
        mobile: 204
      }
    },
    large: {
      width: {
        desktop: 456,
        tablet: 728,
        mobile: 320
      },
      height: {
        desktop: 531,
        tablet: 360,
        mobile: 232
      }
    }
  }

  const currentBreakpoint = computed(() => {
    if (isDesktop.value) return 'desktop'
    if (isTablet.value) return 'tablet'

    return 'mobile'
  })

  const isFullWidth = computed(() => props.blok.width === 'full')
  const imageSizeValue = computed(() => props.blok.imageSize ?? 'large')
  const contentVerticalAlignment = computed(() => props.blok.contentVerticalAlignment ?? 'center')
  const imageIsSmallAndStackedOnTablet = computed(
    () => imageSizeValue.value === 'small' && props.blok.layoutOnTabletDevices === 'stacked'
  )

  const imageSize = computed(() => {
    const size = imageSizeValue.value

    return {
      width: imageDimensions[size].width[currentBreakpoint.value],
      height: imageDimensions[size].height[currentBreakpoint.value]
    }
  })

  const imageProps = computed(() => toAssetImageProps(props.blok.image))

  const containerCva = computed(() =>
    cva('mt-4 md:space-y-0 lg:mt-0', {
      variants: {
        isFullWidth: {
          true: 'w-full',
          false: 'md:mx-auto md:w-10/12'
        },
        layoutOnTabletDevices: {
          row: 'sm:flex sm:items-center',
          stacked: 'gap-x-24 md:flex'
        },
        contentVerticalAlignment: {
          top: 'md:items-start',
          center: 'md:items-center'
        },
        imageSize: {
          small: '',
          large: 'md:mb-4'
        },
        extraBottomMargin: {
          true: 'lg:mb-8',
          false: ''
        }
      }
    })({
      isFullWidth: isFullWidth.value,
      layoutOnTabletDevices: props.blok.layoutOnTabletDevices,
      contentVerticalAlignment: contentVerticalAlignment.value,
      imageSize: imageSizeValue.value,
      extraBottomMargin: props.blok.extraBottomMargin
    })
  )

  const contentWrapperCva = computed(() =>
    cva(
      'flex flex-col justify-center sm:mx-auto md:mb-0 md:w-3/5 lg:w-1/2 md:[&>div:first-child>div:first-child]:mt-0 items-start',
      {
        variants: {
          imageSize: {
            small: 'mb-2',
            large: 'mb-10'
          },
          layoutOnTabletDevices: {
            row: 'sm:w-1/2',
            stacked: 'sm:w-3/4'
          },
          contentVerticalAlignment: {
            top: 'md:justify-start',
            center: 'md:justify-center'
          }
        }
      }
    )({
      imageSize: imageSizeValue.value,
      layoutOnTabletDevices: props.blok.layoutOnTabletDevices,
      contentVerticalAlignment: contentVerticalAlignment.value
    })
  )

  const imageWrapperCva = computed(() =>
    cva('md:w-2/5 lg:flex lg:w-1/2 lg:justify-end self-start', {
      variants: {
        layoutOnTabletDevices: {
          row: 'sm:flex sm:w-1/2 sm:justify-end',
          stacked: 'flex justify-center'
        }
      }
    })({
      layoutOnTabletDevices: props.blok.layoutOnTabletDevices
    })
  )

  const imageCva = computed(() =>
    cva('w-full object-cover', {
      variants: {
        imageSize: {
          large: 'md:w-auto rounded-md lg:rounded-lg',
          small: 'sm:w-auto rounded-sm md:rounded-md'
        },
        isSmallStackedOnTablet: {
          true: 'sm:w-full md:w-auto',
          false: ''
        }
      }
    })({
      imageSize: imageSizeValue.value,
      isSmallStackedOnTablet: imageIsSmallAndStackedOnTablet.value
    })
  )
</script>

<template>
  <section class="md:mx-auto md:w-10/12 lg:w-full">
    <div :class="containerCva">
      <div :class="contentWrapperCva">
        <StoryblokComponent
          v-for="component in blok.content"
          :key="component._uid"
          :blok="component"
          is-nested
        />
      </div>
      <div :class="imageWrapperCva">
        <NuxtImg
          :src="imageProps.src"
          :alt="imageProps.alt"
          :width="imageSize.width"
          :height="imageSize.height"
          :class="imageCva"
          provider="storyblok"
        />
      </div>
    </div>
  </section>
</template>
